import {
    BrowserOAuthClient,
    isExpectedSessionError,
    TokenRevokedError,
    type OAuthSession,
} from '@atproto/oauth-client-browser'
import { PENDING_ACTIVE_ACCOUNT_KEY } from '@/constants/storage'
import { getStoredAppView } from '@/contexts/AppViewContext/AppViewContext'

const BLUESKY_DIDS_KEY = 'mackinac:bluesky_dids'

/** Per-tab record of an in-progress reconnect, written before the OAuth redirect. */
const RECONNECT_KEY = 'mackinac:reconnect'
/** A reconnect record older than this is ignored (and cleared) — bounds a stale guard. */
const RECONNECT_RECORD_TTL_MS = 10 * 60_000

function getHandleResolver(): string {
    return getStoredAppView() === 'blacksky' ? 'https://blacksky.app' : 'https://bsky.social'
}

let client: BrowserOAuthClient | undefined
let clientHandleResolver: string | undefined
let cachedClientMetadata: unknown

/**
 * DIDs the webapp is deliberately revoking (logout, account removal, rejected
 * reconnect callback). The library's onDelete hook fires for these, and treating
 * them as expiries would create a placeholder for an account the user just removed.
 */
const removingDids = new Set<string>()

/**
 * One reconnect redirect per tab. The library's signIn() navigates away, so this
 * flag stays set on success — a second tap (from any surface) is a no-op.
 */
let reconnectInFlight = false

export type SessionExpiredEvent = {
    did: string
    /**
     * 'revoked' — a live record was deleted by a revoke (possibly another tab's
     * reconnect pre-revoke); the consumer should verify before committing.
     * 'expired' — the record is gone / refresh permanently failed; authoritative.
     */
    kind: 'revoked' | 'expired'
}

type ExpiredListener = (event: SessionExpiredEvent) => void
type RestoredListener = (did: string) => void

const expiredListeners = new Set<ExpiredListener>()
const restoredListeners = new Set<RestoredListener>()

export function subscribeSessionExpired(listener: ExpiredListener): () => void {
    expiredListeners.add(listener)
    return () => expiredListeners.delete(listener)
}

function notifySessionExpired(event: SessionExpiredEvent): void {
    for (const listener of expiredListeners) {
        try {
            listener(event)
        } catch (err) {
            console.error('Session expiry listener failed:', err)
        }
    }
}

export function subscribeSessionRestored(listener: RestoredListener): () => void {
    restoredListeners.add(listener)
    return () => restoredListeners.delete(listener)
}

function notifySessionRestored(did: string): void {
    for (const listener of restoredListeners) {
        try {
            listener(did)
        } catch (err) {
            console.error('Session restore listener failed:', err)
        }
    }
}

interface ReconnectRecord {
    did: string
    state: string
    createdAt: number
}

/** Throws when sessionStorage is unavailable — callers surface that to the user. */
function writeReconnectRecord(record: ReconnectRecord): void {
    sessionStorage.setItem(RECONNECT_KEY, JSON.stringify(record))
}

function clearReconnectRecord(): void {
    try {
        sessionStorage.removeItem(RECONNECT_KEY)
    } catch {
        // sessionStorage unavailable
    }
}

function readReconnectRecord(): ReconnectRecord | null {
    let raw: string | null
    try {
        raw = sessionStorage.getItem(RECONNECT_KEY)
    } catch {
        return null
    }
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw) as Partial<ReconnectRecord>
        if (
            typeof parsed?.did !== 'string' ||
            typeof parsed?.state !== 'string' ||
            typeof parsed?.createdAt !== 'number'
        ) {
            clearReconnectRecord()
            return null
        }
        if (Date.now() - parsed.createdAt > RECONNECT_RECORD_TTL_MS) {
            clearReconnectRecord()
            return null
        }
        return parsed as ReconnectRecord
    } catch {
        clearReconnectRecord()
        return null
    }
}

/**
 * Mirrors BrowserOAuthClient.readCallbackParams(). The client is constructed with
 * the library's default responseMode ('fragment'), so the authorization server
 * returns `code`/`error` in location.hash — never in the query string. Reading
 * window.location.search here would make callback detection always false in
 * production and leave the reconnect record uncleared for its full TTL.
 */
function readCallbackParams(): URLSearchParams | null {
    const params = new URLSearchParams(window.location.hash.slice(1))
    if (!params.has('state') || !(params.has('code') || params.has('error'))) return null
    return params
}

/** The library's bfcache rejection message (signInRedirect's 5s back-navigation timer). */
const NAVIGATED_BACK_MESSAGE = 'User navigated back'

/**
 * True when a signIn() rejection is the library cancelling a redirect the user
 * backed out of, rather than a real failure. abortRequest() failures are wrapped
 * in an AggregateError alongside the original cancellation.
 */
function isRedirectCancellation(err: unknown): boolean {
    if (err instanceof AggregateError) return err.errors.some(isRedirectCancellation)
    return err instanceof Error && err.message === NAVIGATED_BACK_MESSAGE
}

interface InitResult {
    client: BrowserOAuthClient | undefined
    result: { session?: OAuthSession; state?: string | null } | undefined
    reconnectOutcome?: ReconnectOutcome
}

export type ReconnectOutcome = 'matched' | 'mismatch' | 'rejected'

let initPromise: Promise<InitResult> | undefined

/**
 * Stored Bluesky DIDs, in the order they were added. Malformed entries are
 * dropped and the sanitized list is written back so the corruption doesn't
 * resurface on every read.
 */
export function getStoredDids(): string[] {
    let parsed: unknown
    try {
        const stored = localStorage.getItem(BLUESKY_DIDS_KEY)
        if (!stored) return []
        parsed = JSON.parse(stored)
    } catch {
        // Corrupt JSON — reset to an empty list
        try {
            localStorage.setItem(BLUESKY_DIDS_KEY, JSON.stringify([]))
        } catch {
            // localStorage unavailable
        }
        return []
    }

    const source = Array.isArray(parsed) ? parsed : []
    const sanitized = source.filter(
        (d): d is string => typeof d === 'string' && d.startsWith('did:')
    )

    if (!Array.isArray(parsed) || sanitized.length !== source.length) {
        try {
            localStorage.setItem(BLUESKY_DIDS_KEY, JSON.stringify(sanitized))
        } catch {
            // localStorage unavailable
        }
    }

    return sanitized
}

function addStoredDid(did: string): void {
    const dids = getStoredDids()
    if (!dids.includes(did)) {
        dids.push(did)
        localStorage.setItem(BLUESKY_DIDS_KEY, JSON.stringify(dids))
    }
}

/** Forget a DID locally, without touching the OAuth client or the session store. */
export function removeStoredDid(did: string): void {
    const dids = getStoredDids().filter(d => d !== did)
    localStorage.setItem(BLUESKY_DIDS_KEY, JSON.stringify(dids))
}

export async function initClient() {
    if (initPromise) return initPromise

    initPromise = (async () => {
        console.log('Initializing BrowserOAuthClient...')
        const url = new URL(window.location.href)

        // Auto-redirect localhost to 127.0.0.1 to match strict OAuth requirements
        if (url.hostname === 'localhost') {
            url.hostname = '127.0.0.1'
            window.location.href = url.href
            return { client: undefined, result: undefined }
        }

        // Detect environment: loopback (HTTP), LAN dev (HTTPS), or production
        const isLoopback = url.hostname === '127.0.0.1' && url.protocol === 'http:'
        const isPrivateIP =
            /^10\./.test(url.hostname) ||
            /^192\.168\./.test(url.hostname) ||
            /^172\.(1[6-9]|2\d|3[01])\./.test(url.hostname)
        const isLocalNetwork = url.hostname.endsWith('.local') || url.hostname.endsWith('.ts.net')
        const isLanDev = !isLoopback && (isPrivateIP || isLocalNetwork)

        const port = url.port ? `:${url.port}` : ''
        const origin = url.origin
        const redirectUrl = origin

        let clientMetadata

        if (isLoopback) {
            // HTTP loopback: use AT Protocol's special loopback client ID format
            const loopbackRedirect = `http://127.0.0.1${port}`
            const encRedirect = encodeURIComponent(loopbackRedirect)
            const encScope = encodeURIComponent('atproto transition:generic')
            const clientId = `http://localhost?redirect_uri=${encRedirect}&scope=${encScope}`
            console.log('DEBUG: Generated Loopback Client ID:', clientId)

            clientMetadata = {
                client_id: clientId,
                client_name: 'Mackinac Web (Local)',
                client_uri: origin,
                logo_uri: `${origin}/assets/logo.png`,
                redirect_uris: [loopbackRedirect],
                scope: 'atproto transition:generic',
                grant_types: ['authorization_code', 'refresh_token'],
                response_types: ['code'],
                token_endpoint_auth_method: 'none',
                application_type: 'web',
                dpop_bound_access_tokens: false,
            }
        } else if (isPrivateIP) {
            // IP addresses are rejected by the AT Proto OAuth library entirely.
            // Skip AT Proto init — Mastodon OAuth still works from this address.
            console.warn(
                `LAN IP detected (${origin}). AT Protocol OAuth is unavailable ` +
                    '(client_id cannot use an IP address). Mastodon OAuth will work.'
            )
            return { client: undefined, result: undefined }
        } else if (isLanDev) {
            // .local or .ts.net hostname over HTTPS — use standard client metadata
            clientMetadata = {
                client_id: `${origin}/client-metadata-local.json`,
                client_name: 'Mackinac Web (LAN Dev)',
                client_uri: origin,
                logo_uri: `${origin}/assets/logo.png`,
                redirect_uris: [redirectUrl],
                scope: 'atproto transition:generic',
                grant_types: ['authorization_code', 'refresh_token'],
                response_types: ['code'],
                token_endpoint_auth_method: 'none',
                application_type: 'web',
                dpop_bound_access_tokens: true,
            }
        } else {
            clientMetadata = {
                client_id: `${origin}/client-metadata.json`,
                client_name: 'Mackinac Web',
                client_uri: origin,
                logo_uri: `${origin}/assets/logo.png`,
                redirect_uris: [redirectUrl],
                scope: 'atproto transition:generic',
                grant_types: ['authorization_code', 'refresh_token'],
                response_types: ['code'],
                token_endpoint_auth_method: 'none',
                application_type: 'web',
                dpop_bound_access_tokens: true,
            }
        }

        const handleResolver = getHandleResolver()
        clientHandleResolver = handleResolver
        cachedClientMetadata = clientMetadata

        // Check if we're returning from an OAuth callback before init processes it.
        // Fragment-based, matching how the library itself reads the callback.
        const callbackParams = readCallbackParams()
        const hasCallbackCode = callbackParams?.has('code') ?? false
        // An authorization-server error return (e.g. access_denied) is also a callback:
        // init() throws OAuthCallbackError for it, and the reconnect record is consumed.
        const hasCallback = callbackParams !== null

        try {
            client = new BrowserOAuthClient({
                handleResolver,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                clientMetadata: clientMetadata as any,
                onDelete: (sub: string, cause: unknown) => {
                    // Revokes the webapp initiated itself are not expiries.
                    if (removingDids.has(sub)) return
                    // This tab's own reconnect pre-revokes the session before storing
                    // the refreshed one — not an expiry either.
                    if (readReconnectRecord()?.did === sub) return
                    notifySessionExpired({
                        did: sub,
                        kind: cause instanceof TokenRevokedError ? 'revoked' : 'expired',
                    })
                },
                onUpdate: (sub: string) => {
                    // The hook's payload is the raw stored record, not an OAuthSession,
                    // so consumers must restore by DID to get a usable session.
                    notifySessionRestored(sub)
                },
            })

            const result: InitResult['result'] = await client.init()
            console.log('BrowserOAuthClient initialized. Result:', result)

            let reconnectOutcome: ReconnectOutcome | undefined
            const record = readReconnectRecord()

            if (record && result?.session && result.state === record.state) {
                const returnedDid = result.session.did
                if (returnedDid === record.did) {
                    reconnectOutcome = 'matched'
                } else if (getStoredDids().includes(returnedDid)) {
                    // The user authorized a different, already-connected account. Its
                    // record was just refreshed by the callback, so keep it live — but
                    // don't make it active and don't clear the original placeholder.
                    reconnectOutcome = 'mismatch'
                    console.warn(
                        `Reconnect for ${record.did} returned already-connected ${returnedDid}; ` +
                            'keeping the refreshed session but leaving the placeholder expired.'
                    )
                } else {
                    // An unknown account would bypass the account limit and the DID pin.
                    reconnectOutcome = 'rejected'
                    console.warn(
                        `Reconnect for ${record.did} returned unknown ${returnedDid}; revoking.`
                    )
                    removingDids.add(returnedDid)
                    try {
                        await client.revoke(returnedDid as `did:${string}`)
                    } catch (err) {
                        console.error('Failed to revoke mismatched reconnect session:', err)
                    } finally {
                        removingDids.delete(returnedDid)
                    }
                    return { client, result: undefined, reconnectOutcome }
                }
            }

            if (result?.session) {
                addStoredDid(result.session.did)
                console.log('Session cached:', result.session.did)

                // A mismatched-but-connected reconnect must not steal the active account.
                if (hasCallbackCode && reconnectOutcome !== 'mismatch') {
                    localStorage.setItem(PENDING_ACTIVE_ACCOUNT_KEY, result.session.did)
                }
            }
            return { client, result, reconnectOutcome }
        } catch (err) {
            console.error('AT Protocol OAuth init failed:', err)
            // Don't clear the client — it may still be usable for individual restore() calls.
            // Don't wipe stored DIDs or IndexedDB — the sessions may be recoverable via
            // client.restore(did), which handles token refresh independently of init().
            // Wiping on any init() failure causes permanent session loss when the error is
            // transient (e.g., network timeout or token refresh failure after backgrounding).
            return { client, result: undefined }
        } finally {
            // Consume the reconnect record only when this init actually processed a
            // callback. Clearing it on every init would let a back-then-forward
            // navigation at the authorization server return as an unpinned login.
            if (hasCallback) clearReconnectRecord()
        }
    })()

    return initPromise
}

export type RestoreResult =
    | { status: 'live'; session: OAuthSession }
    /** Permanently unusable — the library has deleted (or never had) the stored record. */
    | { status: 'expired' }
    /** Recoverable (network blip, no client) — keep the DID and try again later. */
    | { status: 'transient' }

/**
 * Restore a specific Bluesky session by DID, classifying the failure.
 *
 * Note: client.restore() for a DID whose record is missing fires the library's
 * onDelete hook (with a TokenRefreshError) before throwing. Consumers of the
 * expiry events must therefore be idempotent per DID.
 */
export async function restoreSession(did: string): Promise<RestoreResult> {
    if (!client) await initClient()
    // Without a client nothing can be classified as permanently expired.
    if (!client) return { status: 'transient' }

    try {
        const session = await client.restore(did as `did:${string}`)
        return { status: 'live', session }
    } catch (err) {
        console.warn(`Failed to restore session for ${did}:`, err)
        // Don't remove the DID from storage here — the DID is only removed
        // explicitly via logout()/removeAccount(), so an expired account can
        // stay in the switcher as a reconnectable placeholder.
        //
        // isExpectedSessionError is the library's own deleteOnError predicate:
        // when it matches, the stored record is already gone.
        return isExpectedSessionError(err) ? { status: 'expired' } : { status: 'transient' }
    }
}

export interface AllSessionsResult {
    /** Live sessions, in stored-DID order. */
    live: OAuthSession[]
    /** DIDs whose session permanently expired, in stored-DID order. */
    expiredDids: string[]
    reconnectOutcome?: ReconnectOutcome
}

/**
 * Initialize all known Bluesky sessions.
 * 1. client.init() handles the callback session (and the most recent session)
 * 2. Every other stored DID is restored and classified individually
 *
 * Results are returned in stored-DID order so an account keeps its switcher
 * position across expiry and reconnect.
 */
export async function initAllSessions(): Promise<AllSessionsResult> {
    const initResult = await initClient()
    if (!client || !initResult) return { live: [], expiredDids: [] }

    const initSession = initResult.result?.session
    const live: OAuthSession[] = []
    const expiredDids: string[] = []

    for (const did of getStoredDids()) {
        if (initSession && did === initSession.did) {
            live.push(initSession)
            continue
        }
        const result = await restoreSession(did)
        if (result.status === 'live') {
            live.push(result.session)
        } else if (result.status === 'expired') {
            expiredDids.push(did)
        }
        // 'transient' — neither live nor expired; the DID stays stored and is
        // retried on the next load.
    }

    return { live, expiredDids, reconnectOutcome: initResult.reconnectOutcome }
}

/**
 * Start a DID-pinned reconnect for an expired account.
 *
 * Redirects straight to the authorization server for that exact DID — no handle
 * entry, no consent prompt. Resolves false when another reconnect is already in
 * flight in this tab, or when the user backed out of the authorization step
 * (neither is an error to surface); otherwise it does not resolve (the page
 * navigates away). Genuine failures reject.
 */
export async function reconnect(did: string): Promise<boolean> {
    if (reconnectInFlight) return false
    reconnectInFlight = true

    if (!client) await initClient()
    if (!client) {
        reconnectInFlight = false
        throw new Error('Client failed to initialize')
    }

    const array = new Uint8Array(32)
    window.crypto.getRandomValues(array)
    const state = btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')

    try {
        writeReconnectRecord({ did, state, createdAt: Date.now() })
    } catch (err) {
        reconnectInFlight = false
        console.error('Failed to record reconnect:', err)
        throw new Error('Reconnect unavailable: session storage is blocked', { cause: err })
    }

    try {
        // Sign in by DID (not handle) so a reassigned handle can't redirect the
        // reconnect to a different account. No prompt — this is a re-auth.
        await client.signIn(did as `did:${string}`, { state })
        return true
    } catch (err) {
        // signIn() navigates away on success; this is a synchronous failure or the
        // library's 5s "User navigated back" bfcache rejection.
        clearReconnectRecord()
        reconnectInFlight = false
        // Backing out of the authorization step is not a failure: the placeholder
        // stays as it was and is immediately tappable again, with no error shown.
        if (isRedirectCancellation(err)) return false
        throw err
    }
}

export async function login(handle: string) {
    if (!client) await initClient()
    // client may be set even when init() threw — see initClient() catch block.
    // signIn() constructs a fresh authorization URL from clientMetadata and does not
    // depend on init() having succeeded, so it is safe to call here.
    if (!client) throw new Error('Client failed to initialize')

    if (!handle) return

    // Generate random state
    const array = new Uint8Array(32)
    window.crypto.getRandomValues(array)
    const state = btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')

    // Re-check the AppView at sign-in time — the user may have changed it on the login
    // page after the client was initialized. If so, build a fresh client with the correct
    // handleResolver for this sign-in only (the session-management client stays intact).
    const currentResolver = getHandleResolver()
    let signinClient: BrowserOAuthClient = client
    if (currentResolver !== clientHandleResolver && cachedClientMetadata) {
        signinClient = new BrowserOAuthClient({
            handleResolver: currentResolver,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clientMetadata: cachedClientMetadata as any,
        })
    }

    await signinClient.signIn(handle, {
        state: state,
        prompt: 'consent',
    })
}

const deleteDB = (name: string) =>
    new Promise(resolve => {
        const req = window.indexedDB.deleteDatabase(name)
        req.onsuccess = () => resolve(null)
        req.onerror = () => {
            console.error(`Failed to delete DB ${name}:`, req.error)
            resolve(null) // Resolve anyway to continue
        }
        req.onblocked = () => {
            console.warn('DB delete blocked:', name)
            resolve(null) // Resolve anyway
        }
    })

/**
 * Revoke a specific Bluesky session by DID, or all sessions if no DID provided.
 */
export async function logout(did?: string) {
    if (!client) await initClient()

    if (did) {
        // Revoke a specific session. removingDids suppresses the onDelete hook so a
        // deliberate removal isn't mistaken for an expiry.
        removingDids.add(did)
        try {
            console.log('Revoking session for:', did)
            await client!.revoke(did as `did:${string}`)
            console.log('Session revoked:', did)
        } catch (err) {
            console.error('Error revoking session:', err)
        } finally {
            removingDids.delete(did)
        }
        removeStoredDid(did)
    } else {
        // Revoke all sessions
        const dids = getStoredDids()
        dids.forEach(d => removingDids.add(d))
        try {
            for (const d of dids) {
                try {
                    await client!.revoke(d as `did:${string}`)
                } catch (err) {
                    console.error(`Error revoking session ${d}:`, err)
                }
            }
        } finally {
            dids.forEach(d => removingDids.delete(d))
        }
        localStorage.removeItem(BLUESKY_DIDS_KEY)

        // Clear only AtProto-related IndexedDBs
        try {
            if (window.indexedDB && window.indexedDB.databases) {
                const dbs = await window.indexedDB.databases()
                const deletePromises = []

                for (const db of dbs) {
                    if (db.name && (db.name.includes('atproto') || db.name.includes('oauth'))) {
                        console.log('Deleting database:', db.name)
                        deletePromises.push(deleteDB(db.name))
                    }
                }

                if (deletePromises.length > 0) {
                    await Promise.all(deletePromises)
                    console.log('All databases deleted')
                }
            }
        } catch (err) {
            console.error('Error clearing local storage:', err)
        }
    }
}

export function getClient() {
    return client
}

export async function requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage?.persist) {
        const granted = await navigator.storage.persist()
        if (!granted) {
            console.warn('Persistent storage not granted — sessions may be lost on iOS PWA')
        }
        return granted
    }
    return false
}
